import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Panel, SectionTitle, TactLoader } from "../components/Tactical";
import { toast } from "sonner";
import { useT } from "../context/LanguageContext";
import {
  Gamepad2, Users, Trophy, AlertTriangle, MessageSquare,
  Send, CheckCircle2, Activity, Crown,
} from "lucide-react";

function Kpi({ label, value, sub, icon: Icon, accent = "#7C3AED", testId }) {
  return (
    <div className="border border-[#E4E4E7] dark:border-[#2A2A30] p-4 hover:border-[#7C3AED] transition-colors" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <Icon className="w-4 h-4" style={{ color: accent }} />
        {sub != null && (
          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#71717A]">+{sub} 7d</span>
        )}
      </div>
      <div className="font-heading text-3xl uppercase tracking-wider" style={{ color: accent }}>{value}</div>
      <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] mt-1">{label}</div>
    </div>
  );
}

export default function ClassicsAnalyticsTab() {
  const { t } = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [familyFilter, setFamilyFilter] = useState("all");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/admin/classics/analytics")
      .then(({ data: d }) => { if (!cancelled) setData(d); })
      .catch((e) => { if (!cancelled) toast.error(formatApiError(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <TactLoader label={t("clAna.loading")} />;
  if (!data) return null;

  const k = data.kpis;
  const rows = (data.per_game || []).filter(
    (g) => familyFilter === "all" || g.family === familyFilter
  );

  return (
    <div className="space-y-6" data-testid="cl-analytics">
      <SectionTitle kicker={`// ${t("clAna.kicker")}`}>{t("clAna.title")}</SectionTitle>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="cl-ana-kpis">
        <Kpi testId="cl-ana-kpi-qualified" label={t("clAna.kpiQualified")} value={k.qualified_players} icon={Users} accent="#7C3AED" />
        <Kpi testId="cl-ana-kpi-active" label={t("clAna.kpiActive")} value={k.active_players} icon={Activity} accent="#00FF87" />
        <Kpi testId="cl-ana-kpi-games" label={t("clAna.kpiActiveGames")} value={k.active_games} icon={Gamepad2} accent="#7C3AED" />
        <Kpi testId="cl-ana-kpi-requests" label={t("clAna.kpiRequests")} value={k.total_pending_requests} sub={k.requests_last_7d} icon={Send} accent="#EA580C" />
        <Kpi testId="cl-ana-kpi-completed" label={t("clAna.kpiCompleted")} value={k.total_completed_matches} sub={k.completed_last_7d} icon={CheckCircle2} accent="#00FF87" />
        <Kpi testId="cl-ana-kpi-disputes" label={t("clAna.kpiDisputes")} value={k.open_disputes} icon={AlertTriangle} accent="#E11D48" />
      </div>

      {/* Per-game table */}
      <Panel testId="cl-ana-per-game">
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">// {t("clAna.perGameKicker")}</div>
          <div className="flex gap-1 border border-[#E4E4E7] dark:border-[#2A2A30] p-1" data-testid="cl-ana-family-filter">
            {[
              { id: "all", label: t("cl.family.all") },
              { id: "pes", label: "PES" },
              { id: "fifa", label: "FIFA" },
            ].map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFamilyFilter(f.id)}
                data-testid={`cl-ana-family-${f.id}`}
                className={`px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] transition-colors ${familyFilter === f.id ? "bg-[#7C3AED] text-white" : "text-[#71717A] hover:text-[#09090B] dark:hover:text-[#F4F4F5]"}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="cl-ana-table">
            <thead>
              <tr className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A] border-b border-[#E4E4E7] dark:border-[#2A2A30]">
                <th className="px-2 py-2 text-left">{t("clAna.colGame")}</th>
                <th className="px-2 py-2 text-center" title={t("clAna.colPlayers")}><Users className="inline w-3 h-3" /></th>
                <th className="px-2 py-2 text-center text-[#7C3AED]">H</th>
                <th className="px-2 py-2 text-center text-[#00FF87]">G</th>
                <th className="px-2 py-2 text-center text-[#EA580C]" title={t("clAna.colPending")}><Send className="inline w-3 h-3" /></th>
                <th className="px-2 py-2 text-center" title={t("clAna.colCompleted")}><Trophy className="inline w-3 h-3 text-[#00FF87]" /></th>
                <th className="px-2 py-2 text-center text-[#E11D48]" title={t("clAna.colDisputes")}><AlertTriangle className="inline w-3 h-3" /></th>
                <th className="px-2 py-2 text-center" title={t("clAna.colChat")}><MessageSquare className="inline w-3 h-3 text-[#71717A]" /></th>
                <th className="px-2 py-2 text-left">{t("clAna.colTop")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-8 font-mono text-sm text-[#71717A]">[ {t("clAna.empty")} ]</td></tr>
              ) : rows.map((g) => (
                <tr key={g.game_id} className="border-b border-[#E4E4E7]/50 dark:border-[#2A2A30]/50 hover:bg-[#7C3AED]/5" data-testid={`cl-ana-row-${g.game_id}`}>
                  <td className="px-2 py-2">
                    <div className="font-bold">{g.name}</div>
                    <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#71717A]">{g.family} · {g.year}</div>
                  </td>
                  <td className="px-2 py-2 text-center font-mono">{g.players}</td>
                  <td className="px-2 py-2 text-center font-mono text-[#7C3AED]">{g.hosts}</td>
                  <td className="px-2 py-2 text-center font-mono text-[#00FF87]">{g.guests}</td>
                  <td className="px-2 py-2 text-center font-mono text-[#EA580C]">{g.pending_requests}</td>
                  <td className="px-2 py-2 text-center font-mono">{g.completed_matches}</td>
                  <td className={`px-2 py-2 text-center font-mono ${g.disputed_matches > 0 ? "text-[#E11D48] font-bold" : ""}`}>{g.disputed_matches}</td>
                  <td className="px-2 py-2 text-center font-mono text-[#71717A]">{g.chat_messages}</td>
                  <td className="px-2 py-2">
                    {g.top_player ? (
                      <Link to={`/players/${g.top_player.username}`} className="inline-flex items-center gap-1 text-[#FFD700] hover:underline font-bold">
                        <Crown className="w-3 h-3" /> {g.top_player.username}
                        <span className="font-mono text-[10px] text-[#71717A] ml-1">{g.top_player.points}p</span>
                      </Link>
                    ) : (
                      <span className="font-mono text-[10px] text-[#71717A]">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>

      <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#71717A] text-right" data-testid="cl-ana-generated">
        {t("clAna.generatedAt")} {new Date(data.generated_at).toLocaleString()}
      </div>
    </div>
  );
}
